import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';

/**
 * Les deux onglets de l'écran Sessions : **Liste** et **Matière** (§12.1,
 * §12.4, §15).
 *
 * Ils s'appellent ainsi et pas « Sessions » et « Matière » : « "Sessions"
 * répété en onglet sous un `h1` "Sessions" dirait deux fois la même chose »
 * (§15). Ce sont les titres mêmes des deux sections de la spec.
 *
 * ## Un `nav` avec `aria-current`, et PAS un `tablist` ARIA
 *
 * Le choix voyage dans l'URL (`?tab=`), donc un rechargement, un retour arrière
 * ou un lien collé retombent sur le même panneau. Cela fait de chaque onglet une
 * DESTINATION et non l'état d'un widget, et `aria-current="page"` est ce qui le
 * dit - la même marque que porte le rail. C'est l'arbitrage déjà écrit dans
 * `versioned-tabs.tsx`, et il est repris plutôt que redébattu.
 *
 * ## Des boutons et pas des liens
 *
 * Comme `VersionedTabs`, et pour la même raison : ce composant est aveugle à la
 * route qui le monte, TanStack type `to` et `search` contre l'arbre de routes,
 * donc la navigation part par un rappel et c'est l'écran qui l'écrit.
 *
 * ## One bar, two screens (#172)
 *
 * The parcours of a session carries the same bar under its eight figures -
 * Parcours and Variables, « une barre au patron Liste / Matière » (§12.2). So
 * the bar itself is `TabBar`, generic over its tabs and its marks, and
 * `SessionsTabs` is the list's instance of it: two copies of these classes
 * would drift on the first retouch of either.
 */

export const SESSIONS_TABS = [
  { id: 'liste', label: 'Liste' },
  { id: 'matiere', label: 'Matière' },
] as const;

export type SessionsTab = (typeof SESSIONS_TABS)[number]['id'];

/** One tab of a bar: the value its screen writes, and the word it shows. */
export interface TabBarItem<T extends string> {
  readonly id: T;
  readonly label: string;
}

export interface TabBarProps<T extends string> {
  readonly tabs: readonly TabBarItem<T>[];
  readonly active: T;
  readonly onSelect: (tab: T) => void;
  /** The mark of the `nav`. */
  readonly testId: string;
  /** The mark of each button, suffixed with `-<id>`. */
  readonly tabTestId: string;
}

/** Ce qu'un onglet porte quel que soit son état - et rien que ni l'un ni l'autre ne pose. */
const TAB_BASE =
  'inline-flex h-10 cursor-pointer items-center border-b-[3px] px-1 font-semibold text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const TAB_OFF = 'border-transparent text-gray-600 hover:text-bleu-nuit';
const TAB_ON = 'border-bleu-nuit text-bleu-nuit';

export function TabBar<T extends string>({
  tabs,
  active,
  onSelect,
  testId,
  tabTestId,
}: TabBarProps<T>) {
  return (
    <nav aria-label="Sections" data-testid={testId} className="border-gray-200 border-b">
      <ul className="-mb-px flex flex-wrap gap-6">
        {tabs.map((tab) => {
          const current = tab.id === active;
          return (
            <li key={tab.id}>
              <button
                type="button"
                data-testid={`${tabTestId}-${tab.id}`}
                // `page` et pas `true` : le panneau qu'il ouvre est adressable.
                aria-current={current ? 'page' : undefined}
                onClick={() => onSelect(tab.id)}
                className={`${TAB_BASE} ${current ? TAB_ON : TAB_OFF}`}
              >
                {tab.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export interface SessionsTabsProps {
  readonly active: SessionsTab;
  readonly onSelect: (tab: SessionsTab) => void;
}

export function SessionsTabs({ active, onSelect }: SessionsTabsProps) {
  return (
    <TabBar
      tabs={SESSIONS_TABS}
      active={active}
      onSelect={onSelect}
      testId={SESSIONS_TEST_IDS.tabs}
      tabTestId="sessions-onglet"
    />
  );
}
